import { ErrorMessage } from "@hookform/error-message";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import "react-medium-image-zoom/dist/styles.css";
import { NumericFormat } from "react-number-format";
import { useHistory, useParams } from "react-router";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Input, Modal, ModalBody, Table } from 'reactstrap';
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Row,
  RSelect,
} from "../../components/Component";
import { getConfig } from "../../config";
import { GET, POST, PUT } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { INVENTORY_TYPE, MATERIAL_CATEGORY_GROUP } from "../../utils/Utils";

const { company } = getConfig()

const MATERIAL_CATEGORY_GROUP_OPTIONS = Object.keys(MATERIAL_CATEGORY_GROUP).map((c) => ({
  label: MATERIAL_CATEGORY_GROUP[c],
  value: c,
}));


const INVENTORY_TYPE_OPTIONS = Object.keys(INVENTORY_TYPE).map((c) => ({
  label: INVENTORY_TYPE[c],
  value: c,
}));

const MATERIAL_INVENTORY_REQUEST_DETAIL = {
  quantity: null,
  unitPrice: null,
  price: null,
  materialId: null,
  description: null,
}

const MATERIAL_INVENTORY_REQUEST_DEFAULT = {
  date: moment().format('YYYY-MM-DD'),
  type: 'IN',
  description: null,
  percentTL: 50,
  percentCT: 50,
  categoryGroup: null,
  warehouseId: null,
  buyer: null,
  isUsedImmediately: true,
  materialInventoryRequestDetails: [
    structuredClone(MATERIAL_INVENTORY_REQUEST_DETAIL)
  ]
};

const MaterialInventoryRequestUpsert = () => {
  const history = useHistory();
  const { id } = useParams();
  const { search } = useLocation();
  const duplicateId = new URLSearchParams(search).get("duplicateId");
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [materialSummary, setMaterialSummary] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [dateConfirm, setDateConfirm] = useState(moment().format('YYYY-MM-DD'));

  const [timeNow, setTimeNow] = useState();
  const [view, setView] = useState({
    confirm: false,
    unConfirm: false,
    approve: false,
  });

  const toggle = (type) => {
    setView({
      ...view,
      [type]: !view[type],
    });
  };

  // function to close the form modal
  const onFormCancel = () => {
    setView({
      confirm: false,
      unConfirm: false,
      approve: false,
    });
  };


  const resetData = () => {
    onFormCancel();
    setTimeNow(moment().valueOf());
  };

  const methodForm = useForm({
    defaultValues: structuredClone({
      ...MATERIAL_INVENTORY_REQUEST_DEFAULT,
      ...(company === 'TL' ? {
        percentTL: 100,
        percentCT: 0,
      } : {}),
      ...(company === 'CT' ? {
        percentTL: 0,
        percentCT: 100,
      } : {})
    }),
  });
  const { errors, register, handleSubmit, reset, getValues, control, watch, setValue, formState: { isDirty } } = methodForm;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'materialInventoryRequestDetails'
  })
  const [data, setData] = useState()
  const isEdit = id !== "create";

  useEffect(() => {

    GET(`material-inventory/summary-inventory`, {
      fromDate: moment().startOf('month').format('YYYY-MM-DD'),
      toDate: moment().format('YYYY-MM-DD'),
    }, true)
      .then(({ data: { rows } }) => {
        setMaterialSummary([...rows]);
      })


    GET("/warehouse", {}, true).then((resp) => {
      setWarehouseOptions(
        resp.data?.rows?.map(
          (c) =>
          ({
            label: c.name,
            value: c.id,
            data: c,
          })
        )
      );
    });

    GET("/user", {}, true).then((resp) => {
      setUserOptions(
        resp.data?.map((c) => ({
          label: c.name,
          value: c.id,
          data: c,
        }))
      );
    });

    GET("/material", {}, true).then((resp) => {
      setMaterialOptions(
        resp.data?.map(
          (c) =>
          ({
            label: `${c.name}(${c.unit})`,
            value: c.id,
            data: c,
          })
        )
      );
    });

    GET("/pn-customer", {}, true).then((resp) => {
      setCustomerOptions(
        resp.data?.map(
          (c) =>
          ({
            label: c.name,
            value: c.id,
            data: c,
          })
        )
      );
    });

  }, []);

  useEffect(() => {
    if (isEdit) {
      GET(`material-inventory/${id}`, {}, true).then(({ data }) => {
        reset(data);
        setData(data)
      });
    }
    if (duplicateId) {
      GET(`material-inventory/${duplicateId}`, {}, true).then(({ data: sourceMIR }) => {
        const {
          id,
          approvedAt,
          approvedBy,
          confirmedAt,
          confirmedBy,
          requestedBy,
          status,
          total,
          materialInventoryRequestDetails
        } = sourceMIR
        materialInventoryRequestDetails.forEach(materialInventoryRequestDetail => {
          delete materialInventoryRequestDetail.id;
        })
        reset({
          ...sourceMIR,
          materialInventoryRequestDetails
        });
      });
    }
  }, [timeNow])
  const onHandleSubmit = () => {
    const {
      date,
      type,
      description,
      percentTL,
      percentCT,
      categoryGroup,
      customerId,
      warehouseId,
      buyer,
      materialInventoryRequestDetails,
      isUsedImmediately
    } = getValues();

    const formData = {
      date,
      type,
      description,
      percentTL,
      percentCT,
      categoryGroup,
      customerId,
      warehouseId,
      buyer,
      materialInventoryRequestDetails,
      isUsedImmediately
    };

    const request = isEdit
      ? PUT(`material-inventory/${id}`, formData, {}, true)
      : POST("material-inventory", { ...formData }, {}, true);

    request
      .then(({ data }) => {
        if (isEdit) {
          toast.success("Cập nhật thành công");
          resetData()
        } else {
          toast.success("Thêm thành công");
          history.push(`/material-inventory-request/${data.id}`)
        }

      })
      .catch(({ response }) => {
        toast.error(response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại.");
      })
      .finally(() => { });
  };

  const type = watch('type')
  const inType = type === 'IN';
  const watchMaterialInventoryRequestDetails = watch('materialInventoryRequestDetails')

  const {
    materialInventoryRequestDetails,
    summary
  } = useMemo(() => {
    const summary = {
      price: 0,
      quantity: 0,
      unitPrice: 0,
    }
    const newFields = []
    fields.forEach((e, i) => {
      const temp = {
        ...e,
        ...watchMaterialInventoryRequestDetails?.[i],
        arrIndex: i
      };
      const { quantity, unitPrice, warehouseId, materialId } = temp
      temp.price = quantity * unitPrice || null;
      if (data?.status !== 'CONFIRMED' && warehouseId && materialId) {
        const actualQuantity = materialSummary?.find(e => e.warehouseId === warehouseId && e.materialId === materialId)?.actualQuantity;
        if (temp.actualQuantity < quantity) {
          temp.actualQuantity = actualQuantity
          temp.oos = true;
        }
      } else {
        temp.actualQuantity = undefined
        temp.oos = undefined
      }


      summary.price += quantity * unitPrice || 0;
      summary.quantity += quantity || 0;
      summary.unitPrice += unitPrice || 0;
      newFields.push(temp)
    })
    return {
      materialInventoryRequestDetails: newFields,
      summary
    }
  }, [watchMaterialInventoryRequestDetails, fields, type, data, materialSummary])


  const approveRequest = () => {
    PUT(`material-inventory/approve/${id}`, {}, {}, true)
      .then((res) => {
        toast.success("duyệt thành công");
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      });
  };

  const confirmRequest = () => {
    PUT(`material-inventory/confirm/${id}`, {
      date: dateConfirm
    }, {}, true)
      .then((res) => {
        toast.success("Chốt thành công");
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      });
  };

  const unConfirmRequest = () => {
    PUT(`material-inventory/unconfirm/${id}`, {}, {}, true)
      .then((res) => {
        toast.success("Huỷ chốt thành công");
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      });
  };

  return (
    <React.Fragment>
      <Head title="Thêm/sửa yêu cầu vật tư"></Head>

      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>{(!isEdit ? "Thêm" : "Sửa") + " yêu cầu vật tư"}</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div className="toggle-expand-content">
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div id="DataTables_Table_1_wrapper" className="dataTables_wrapper dt-bootstrap4 no-footer">
                <div className="row justify-between g-2">
                  <div className="col-8 col-sm-6 text-start"></div>
                  <div className="col-5 col-sm-6 text-end">
                    <div className="datatable-filter">
                      <div className="d-flex justify-content-end g-2">
                        <div className="dataTables_length" id="DataTables_Table_1_length"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="my-3">
                  <FormProvider {...methodForm}>
                    <form onSubmit={handleSubmit(onHandleSubmit)}>
                      <div className="mt-4">
                        <h5>
                          Tổng quát
                        </h5>

                        <Row className="g-2">
                          <Col size="6">
                            <div className="form-group">
                              <label className="form-label" >
                                Loại <span className="text-danger">*</span>
                              </label>
                              <div className="form-control-wrap">
                                <Controller
                                  control={control}
                                  name="type"
                                  rules={{ required: "Vui lòng nhập thông tin" }}
                                  render={({ onChange, value, name, ref }) => (
                                    <RSelect
                                      ref={ref}
                                      name={name}
                                      options={INVENTORY_TYPE_OPTIONS}
                                      value={
                                        INVENTORY_TYPE_OPTIONS.find((c) => c.value === value) || null
                                      }
                                      onChange={(val) => {
                                        onChange(val.value);
                                      }}
                                    />
                                  )}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  name="type"
                                  render={({ message }) => (
                                    <span className="invalid">{message}</span>
                                  )}
                                />
                              </div>
                            </div>
                          </Col>

                          <Col size="6">
                            <div className="form-group">
                              <label className="form-label" >
                                Ngày yêu cầu <span className="text-danger">*</span>
                              </label>
                              <div className="form-control-wrap">
                                <input
                                  className="form-control"
                                  type="date"
                                  name="date"
                                  max={moment().format("YYYY-MM-DD")}

                                  ref={register({
                                    required: "Vui lòng nhập thông tin",
                                  })}
                                />
                                <ErrorMessage errors={errors} name="date" render={({ message }) => <span className="invalid">{message}</span>} />
                              </div>
                            </div>
                          </Col>
                          <Col size="6">
                            <div className="form-group">
                              <label className="form-label" >
                                Nội dung yêu cầu <span className="text-danger">*</span>
                              </label>
                              <div className="form-control-wrap">
                                <input
                                  className="form-control"
                                  name="description"
                                  ref={register({
                                    required: "Vui lòng nhập thông tin",
                                  })}
                                />
                                <ErrorMessage errors={errors} name="description" render={({ message }) => <span className="invalid">{message}</span>} />
                              </div>
                            </div>
                          </Col>

                          <Col size="6">
                            <div className="form-group">
                              <label className="form-label" htmlFor="customer">
                                Kho
                                <span className="text-danger">*</span>
                              </label>
                              <div className="form-control-wrap">
                                <Controller
                                  control={control}
                                  name={`warehouseId`}
                                  rules={{ required: "Vui lòng nhập thông tin" }}
                                  render={({ onChange, value }) => (
                                    <RSelect
                                      options={warehouseOptions}
                                      value={
                                        warehouseOptions.find((c) => c.value === value) || null
                                      }
                                      onChange={(val) => {
                                        onChange(val.value);
                                      }}
                                    />
                                  )}
                                />

                                <ErrorMessage
                                  errors={errors}
                                  name={`warehouseId`}
                                  render={({ message }) => (
                                    <span className="invalid">{message}</span>
                                  )}
                                />
                              </div>
                            </div>
                          </Col>
                          {inType && <>
                            {['PN'].includes(company) ? <Col size="6">
                              <div className="form-group">
                                <label className="form-label" >
                                  Nhà cung cấp công nợ
                                  {['APPROVED', 'CONFIRMED'].includes(data?.status) && <span className="text-danger">*</span>}

                                </label>
                                <div className="form-control-wrap">
                                  <Controller
                                    control={control}
                                    name="customerId"
                                    rules={['APPROVED', 'CONFIRMED'].includes(data?.status) ? { required: "Vui lòng nhập thông tin" } : {}}
                                    render={({ onChange, value, name, ref }) => (
                                      <RSelect
                                        ref={ref}
                                        name={name}
                                        options={customerOptions}
                                        value={
                                          customerOptions.find((c) => c.value === value) || null
                                        }
                                        onChange={(val) => {
                                          onChange(val.value);
                                        }}
                                      />
                                    )}
                                  />
                                  <ErrorMessage
                                    errors={errors}
                                    name="customerId"
                                    render={({ message }) => (
                                      <span className="invalid">{message}</span>
                                    )}
                                  />
                                </div>
                              </div>
                            </Col> : <></>}
                            <Col size="6">
                              <div className="form-group">
                                <label className="form-label" >
                                  Danh mục <span className="text-danger">*</span>
                                </label>
                                <div className="form-control-wrap">
                                  <Controller
                                    control={control}
                                    name="categoryGroup"
                                    rules={{ required: "Vui lòng nhập thông tin" }}
                                    render={({ onChange, value, name, ref }) => (
                                      <RSelect
                                        ref={ref}
                                        name={name}
                                        options={MATERIAL_CATEGORY_GROUP_OPTIONS}
                                        value={
                                          MATERIAL_CATEGORY_GROUP_OPTIONS.find((c) => c.value === value) || null
                                        }
                                        onChange={(val) => {
                                          onChange(val.value);
                                        }}
                                      />
                                    )}
                                  />
                                  <ErrorMessage
                                    errors={errors}
                                    name="categoryGroup"
                                    render={({ message }) => (
                                      <span className="invalid">{message}</span>
                                    )}
                                  />
                                </div>
                              </div>
                            </Col>
                            <Col md="6">
                              <div className="form-group">
                                <label className="form-label" htmlFor="customer">
                                  Người đi {inType ? 'mua' : 'lấy'}
                                  <span className="text-danger">*</span>
                                </label>
                                <div className="form-control-wrap">
                                  <Controller
                                    control={control}
                                    name="buyer"
                                    render={({ onChange, value, name, ref }) => (
                                      <RSelect
                                        classNamePrefix="form-control"
                                        inputRef={ref}
                                        name={name}
                                        options={userOptions}
                                        value={userOptions.find(
                                          (c) => c.value === value
                                        )}
                                        onChange={(val) => onChange(val.value)}
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                            </Col>

                            <Col size="6" className={['TL', 'CT'].includes(company) ? 'd-none' : ''}>
                              <div className="form-group">
                                <label className="form-label" >
                                  % chi phí cho TL <span className="text-danger">*</span>
                                </label>
                                <div className="form-control-wrap">
                                  <Controller
                                    control={control}
                                    name="percentTL"
                                    rules={{ required: "Vui lòng nhập thông tin" }}
                                    render={({ onChange, value, name, ref }) => (
                                      <NumericFormat
                                        className="form-control"
                                        inputRef={ref}
                                        type="text"
                                        value={value}
                                        thousandsGroupStyle="thousand"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        max={100}
                                        min={0}
                                        onValueChange={(val) => {
                                          onChange(val.floatValue || 0)
                                          setValue('percentCT', Number((100 - (val.floatValue || 0)).toFixed(2)))
                                        }}
                                      />
                                    )}
                                  />
                                  <ErrorMessage
                                    errors={errors}
                                    name="percentTL"
                                    render={({ message }) => (
                                      <span className="invalid">{message}</span>
                                    )}
                                  />
                                </div>
                              </div>
                            </Col>
                            <Col size="6" className={['TL', 'CT'].includes(company) ? 'd-none' : ''}>
                              <div className="form-group">
                                <label className="form-label" >
                                  % chi phí cho CT <span className="text-danger">*</span>
                                </label>
                                <div className="form-control-wrap">
                                  <Controller
                                    control={control}
                                    name="percentCT"
                                    rules={{ required: "Vui lòng nhập thông tin" }}
                                    render={({ onChange, value, name, ref }) => (
                                      <NumericFormat
                                        className="form-control"
                                        inputRef={ref}
                                        type="text"
                                        value={value}
                                        thousandsGroupStyle="thousand"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        onValueChange={(val) => {
                                          onChange(val.floatValue)
                                        }}
                                        disabled={true}
                                      />
                                    )}
                                  />
                                  <ErrorMessage
                                    errors={errors}
                                    name="percentCT"
                                    render={({ message }) => (
                                      <span className="invalid">{message}</span>
                                    )}
                                  />
                                </div>
                              </div>
                            </Col>
                            <Col md="6">
                              <div className="form-group">
                                <label className="form-label" htmlFor="customer">
                                  Mua hàng sử dụng liền
                                </label>
                                <div className="form-control-wrap">
                                  <Controller
                                    control={control}
                                    name="isUsedImmediately"
                                    render={({ onChange, value, name }) => <input type="checkbox" checked={!!value} name={name} onChange={(e) => onChange(e.target.checked)} />}
                                  />
                                </div>
                              </div>
                            </Col>
                          </>
                          }

                        </Row>
                      </div>

                      <div className="mt-4">
                        <h5 className="d-flex justify-content-between">
                          Chi tiết yêu cầu
                          <Button type='button' className={`btn btn-primary`} onClick={() => append(structuredClone(MATERIAL_INVENTORY_REQUEST_DETAIL))}>
                            Thêm chi tiết
                          </Button>
                        </h5>
                        <Row className="g-2">
                          <Table>
                            <thead>
                              <tr>
                                <th>Nội dung<span className="text-danger">*</span></th>
                                <th>Vật tư<span className="text-danger">*</span></th>
                                <th>Số lượng<span className="text-danger">*</span></th>
                                <th>Đơn giá</th>
                                <th>Giá</th>
                                <th>Hành động</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td colSpan={2}>
                                  Tổng cộng
                                </td>
                                <td>
                                  <NumericFormat
                                    displayType={'text'}
                                    value={summary?.quantity}
                                    thousandsGroupStyle="thousand"
                                    thousandSeparator=","
                                    decimalScale={2}
                                  />
                                </td>
                                <td>
                                  <NumericFormat
                                    displayType={'text'}
                                    value={summary?.unitPrice}
                                    thousandsGroupStyle="thousand"
                                    thousandSeparator=","
                                    decimalScale={2}
                                  />
                                </td>
                                <td>
                                  <NumericFormat
                                    displayType={'text'}
                                    value={summary?.price || 0}
                                    thousandsGroupStyle="thousand"
                                    thousandSeparator=","
                                    decimalScale={2}
                                  />
                                </td>
                                <td>

                                </td>
                              </tr>
                              {materialInventoryRequestDetails.map((materialInventoryRequestDetail) =>
                                <tr key={materialInventoryRequestDetail.id}>
                                  <td>
                                    <input
                                      defaultValue={materialInventoryRequestDetail.description}
                                      className="form-control"
                                      name={`materialInventoryRequestDetails.${materialInventoryRequestDetail.arrIndex}.description`}
                                      ref={register({
                                        required: "Vui lòng nhập thông tin",
                                      })}
                                    />
                                    <ErrorMessage errors={errors} name={`materialInventoryRequestDetails.${materialInventoryRequestDetail.arrIndex}.description`} render={({ message }) => <span className="invalid">{message}</span>} />
                                  </td>
                                  <td>
                                    <Controller
                                      control={control}
                                      defaultValue={materialInventoryRequestDetail.materialId}
                                      name={`materialInventoryRequestDetails.${materialInventoryRequestDetail.arrIndex}.materialId`}
                                      rules={{ required: "Vui lòng nhập thông tin" }}
                                      render={({ onChange, value }) => (
                                        <RSelect
                                          options={materialOptions}
                                          value={
                                            materialOptions.find((c) => c.value === value) || null
                                          }
                                          onChange={(val) => {
                                            onChange(val.value);
                                          }}
                                        />
                                      )}
                                    />
                                    <ErrorMessage
                                      errors={errors}
                                      name={`materialInventoryRequestDetails.${materialInventoryRequestDetail.arrIndex}.materialId`}
                                      render={({ message }) => (
                                        <span className="invalid">{message}</span>
                                      )}
                                    />
                                  </td>
                                  <td>
                                    <Controller
                                      defaultValue={materialInventoryRequestDetail.quantity}
                                      control={control}
                                      name={`materialInventoryRequestDetails.${materialInventoryRequestDetail.arrIndex}.quantity`}
                                      rules={{ required: "Vui lòng nhập thông tin" }}
                                      render={({ onChange, value }) => (
                                        <NumericFormat
                                          className="form-control"
                                          type="text"
                                          value={value}
                                          thousandsGroupStyle="thousand"
                                          thousandSeparator=","
                                          decimalScale={2}
                                          onValueChange={(val) => {
                                            onChange(val.floatValue)
                                          }}
                                        />
                                      )}
                                    />
                                    <ErrorMessage
                                      errors={errors}
                                      name={`materialInventoryRequestDetails.${materialInventoryRequestDetail.arrIndex}.quantity`}
                                      render={({ message }) => (
                                        <span className="invalid">{message}</span>
                                      )}
                                    />
                                    {materialInventoryRequestDetail.actualQuantity !== undefined && !inType && <div>
                                      <span style={materialInventoryRequestDetail.oos ? { color: 'red' } : {}}>Kho còn:  <NumericFormat
                                        displayType={"text"}
                                        value={materialInventoryRequestDetail.actualQuantity || 0}
                                        thousandsGroupStyle="thousand"
                                        thousandSeparator=","
                                        decimalScale={2}
                                      /></span>
                                    </div>}
                                  </td>
                                  <td>
                                    <Controller
                                      defaultValue={materialInventoryRequestDetail.unitPrice}
                                      control={control}
                                      name={`materialInventoryRequestDetails.${materialInventoryRequestDetail.arrIndex}.unitPrice`}
                                      render={({ onChange, value }) => (
                                        <NumericFormat
                                          className="form-control"
                                          type="text"
                                          value={value}
                                          thousandsGroupStyle="thousand"
                                          thousandSeparator=","
                                          decimalScale={2}
                                          onValueChange={(val) => {
                                            onChange(val.floatValue)
                                          }}
                                        />
                                      )}
                                    />
                                  </td>
                                  <td>
                                    {materialInventoryRequestDetail.price
                                      ? <NumericFormat
                                        key={`materialInventoryRequestDetails.${materialInventoryRequestDetail.arrIndex}.price`}
                                        className="form-control"
                                        type="text"
                                        value={materialInventoryRequestDetail.price || undefined}
                                        thousandsGroupStyle="thousand"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        disabled
                                      /> : <NumericFormat
                                        className="form-control"
                                        type="text"
                                        value={undefined}
                                        thousandsGroupStyle="thousand"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        disabled
                                      />}
                                  </td>
                                  <td>
                                    <Button type='button' className={`btn btn-danger`} onClick={() => remove(materialInventoryRequestDetail.arrIndex)}>
                                      <Icon name={'trash'}></Icon>
                                    </Button>
                                  </td>
                                </tr>

                              )}
                            </tbody>
                          </Table>
                        </Row>
                      </div>
                      <Row className="g-3 ">
                        <Col size="6" className="mt-3">
                          <Button color="primary" type="submit">
                            <span>Lưu</span>
                          </Button>
                          {
                            !isDirty && <>
                              {data?.status === 'REQUESTED' && <Button onClick={() => toggle('approve')} color="primary" type="button" className={`ml-2`}>
                                <span>Duyệt</span>
                              </Button>}
                              {company === 'PN' && <>
                                {data?.status === 'APPROVED' && <Button onClick={() => toggle('confirm')} color="primary" type="button" className={`ml-2`}>
                                  <span>Chốt công nợ</span>
                                </Button>}
                                {data?.status === 'CONFIRMED' && <Button onClick={() => toggle('unConfirm')} color="primary" type="button" className={`ml-2`}>
                                  <span>Huỷ chốt công nợ</span>
                                </Button>}
                              </>}
                            </>
                          }
                        </Col>
                      </Row>
                    </form>
                  </FormProvider>
                </div>
              </div>
            </div>
          </div>
        </Block>
        <Modal isOpen={view.approve} toggle={() => onFormCancel()} >
          <ModalBody className="modal-body-lg text-center">
            <div className="nk-modal">
              <h4 className="nk-modal-title">
                Bạn có chắc muốn duyệt yêu cầu này không ?{" "}
              </h4>
              <div className="nk-modal-action mt-5 d-flex justify-content-around">
                <Button
                  color="light"
                  size="lg"
                  className="btn-mw mr-5"
                  onClick={() => onFormCancel()}
                >
                  Huỷ bỏ
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  className="btn-mw"
                  onClick={(ev) => {
                    ev.preventDefault();
                    approveRequest()
                  }}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={view.unConfirm} toggle={() => onFormCancel()} >
          <ModalBody className="modal-body-lg text-center">
            <div className="nk-modal">
              <h4 className="nk-modal-title">
                Bạn có chắc muốn bỏ chốt công nợ này không ?{" "}
              </h4>
              <div className="nk-modal-action mt-5 d-flex justify-content-around">
                <Button
                  color="light"
                  size="lg"
                  className="btn-mw mr-5"
                  onClick={() => onFormCancel()}
                >
                  Huỷ bỏ
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  className="btn-mw"
                  onClick={(ev) => {
                    ev.preventDefault();
                    unConfirmRequest()
                  }}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={view.confirm} toggle={() => onFormCancel()} >
          <ModalBody className="modal-body-lg text-center">
            <div className="nk-modal">
              <h4 className="nk-modal-title">
                Bạn có chắc muốn chốt công nợ này không ?{" "}
              </h4>
              <div>
                <Input type="date" value={dateConfirm} onChange={e => setDateConfirm(e.target.value)} />
              </div>
              <div className="nk-modal-action mt-5 d-flex justify-content-around">
                <Button
                  color="light"
                  size="lg"
                  className="btn-mw mr-5"
                  onClick={() => onFormCancel()}
                >
                  Huỷ bỏ
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  className="btn-mw"
                  onClick={(ev) => {
                    ev.preventDefault();
                    confirmRequest()
                  }}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment >
  );
};

export default MaterialInventoryRequestUpsert;
